<template>
  <div class="vc-section vc-card vc-card--compact vc-hmc" :class="{ 'vc-hmc--collapsed': !callControl.is_hmc }">
    <div class="vc-hmc__head">
      <span class="vc-hmc__title">人机协同</span>
      <div class="vc-hmc__switch">
        <span class="vc-hmc__switch-label">{{ callControl.is_hmc ? '开启' : '关闭' }}</span>
        <i-switch v-model="callControl.is_hmc" />
      </div>
    </div>

    <div v-show="callControl.is_hmc" class="vc-hmc__body">
      <div class="vc-hmc-field">
        <div class="vc-hmc-field__row vc-hmc-field__row--mode">
          <span class="vc-hmc-field__label vc-hmc-field__label--inline">触发模式</span>
          <div class="vc-hmc-mode-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              class="vc-hmc-mode-tabs__btn"
              :class="{ 'is-active': hmcConfig.trigger_mode === HMC_TRIGGER_MODE.LLM }"
              @click="setTriggerMode(HMC_TRIGGER_MODE.LLM)"
            >
              大模型控制
            </button>
            <button
              type="button"
              role="tab"
              class="vc-hmc-mode-tabs__btn"
              :class="{ 'is-active': hmcConfig.trigger_mode === HMC_TRIGGER_MODE.KEYWORD }"
              @click="setTriggerMode(HMC_TRIGGER_MODE.KEYWORD)"
            >
              关键词控制
            </button>
          </div>
        </div>

        <div class="vc-hmc-field__control vc-hmc-field__control--mode">
          <div v-if="hmcConfig.trigger_mode === HMC_TRIGGER_MODE.LLM" class="vc-hmc-prompt-box">
            <Input
              v-model="hmcConfig.llm_prompt"
              type="textarea"
              :rows="8"
              :maxlength="10000"
              :placeholder="HMC_LLM_PROMPT_PLACEHOLDER"
              class="vc-hmc-prompt-box__textarea"
            />
            <div class="vc-hmc-prompt-box__count">{{ llmPromptLength }}/10000</div>
          </div>

          <KeywordTagEditor
            v-else
            :value="hmcKeywords"
            :disabled="!callControl.is_hmc"
            @input="onHmcKeywordsInput"
          />
        </div>
      </div>

      <div class="vc-hmc-field">
        <div class="vc-hmc-field__label">
          转接内容
          <span class="vc-hmc-field__tag">选填</span>
        </div>
        <div class="vc-hmc-field__control">
          <div class="vc-hmc-transfer-wrap">
            <div class="vc-hmc-transfer-editor">
              <Input
                v-model="hmcConfig.transfer.text"
                type="textarea"
                :rows="4"
                :maxlength="500"
                placeholder="选填，为空时执行无感知转接（直接转接，不播报提示语）"
                class="vc-hmc-transfer-wrap__input"
              />
              <div class="vc-hmc-transfer-toolbar">
                <Button
                  v-if="!transferAudioName"
                  class="vc-opening-tool-btn"
                  :loading="uploadingTransfer"
                  @click="triggerTransferUpload"
                >
                  <Icon type="ios-cloud-upload-outline" size="14" />
                  上传音频
                </Button>
                <div v-else class="vc-audio-tag">
                  <span class="vc-audio-tag__name" :title="transferAudioName">{{ transferAudioName }}</span>
                  <button type="button" class="vc-audio-tag__del" title="删除录音" @click="clearTransferAudio">
                    <Icon type="ios-trash-outline" size="14" />
                  </button>
                </div>
              </div>
            </div>
            <div class="vc-hmc-transfer-count">{{ transferTextLength }}/500</div>
          </div>

          <div class="vc-hmc-transfer-tip">
            <div class="vc-hmc-transfer-tip__main">
              <Icon type="ios-information-circle" class="vc-hmc-transfer-tip__icon" size="16" />
              <p class="vc-hmc-transfer-tip__text">
                转接内容为空时，将执行无感知转接（直接转接，不播报提示语）。如需转接前播报，可填入：稍等，正在为您转接人工坐席，请稍后！
              </p>
            </div>
            <button type="button" class="vc-hmc-transfer-tip__link" @click="fillTransferExample">
              填入示例
            </button>
          </div>
        </div>
      </div>

      <div class="vc-hmc-field vc-hmc-field--failure">
        <div class="vc-hmc-field__label vc-hmc-field__label--block">失败后执行</div>
        <div class="vc-hmc-field__control">
          <RadioGroup v-model="hmcConfig.on_failure">
            <Radio :label="HMC_ON_FAILURE.HANGUP">挂断</Radio>
            <Radio :label="HMC_ON_FAILURE.LLM_CONTINUE">大模型继续应答</Radio>
          </RadioGroup>
        </div>
      </div>
    </div>

    <input
      ref="transferAudioInput"
      type="file"
      accept=".wav,audio/wav,audio/x-wav"
      class="vc-hidden-file"
      @change="handleTransferAudioChange"
    />
  </div>
</template>

<script>
import {
  createDefaultHmcConfig,
  ensureHmcConfig,
  getAudioFileName,
  HMC_LLM_PROMPT_PLACEHOLDER,
  HMC_ON_FAILURE,
  HMC_TRANSFER_EXAMPLE,
  HMC_TRIGGER_MODE,
} from '../../utils/callControlHelpers'
import { uploadAudioFile } from './audioUploadHelper'
import KeywordTagEditor from '../shared/KeywordTagEditor.vue'

export default {
  name: 'HumanMachineCollaborationSection',
  components: { KeywordTagEditor },
  props: {
    callControl: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      HMC_TRIGGER_MODE,
      HMC_ON_FAILURE,
      HMC_LLM_PROMPT_PLACEHOLDER,
      uploadingTransfer: false,
    }
  },
  computed: {
    hmcConfig() {
      return this.callControl.hmc_config
    },
    llmPromptLength() {
      return String(this.hmcConfig && this.hmcConfig.llm_prompt ? this.hmcConfig.llm_prompt : '').length
    },
    transferTextLength() {
      const t = this.hmcConfig && this.hmcConfig.transfer ? this.hmcConfig.transfer.text : ''
      return String(t || '').length
    },
    transferAudioName() {
      if (!this.hmcConfig || !this.hmcConfig.transfer) return ''
      return getAudioFileName(this.hmcConfig.transfer.audio_file_path)
    },
    hmcKeywords() {
      if (!this.hmcConfig) return []
      return Array.isArray(this.hmcConfig.keywords) ? this.hmcConfig.keywords : []
    },
  },
  mounted() {
    if (!this.callControl.hmc_config || typeof this.callControl.hmc_config !== 'object') {
      this.$set(this.callControl, 'hmc_config', createDefaultHmcConfig())
    }
    ensureHmcConfig(this.callControl)
  },
  methods: {
    setTriggerMode(mode) {
      if (!this.hmcConfig) return
      this.$set(this.hmcConfig, 'trigger_mode', mode)
    },
    onHmcKeywordsInput(list) {
      if (!this.hmcConfig) return
      this.$set(this.hmcConfig, 'keywords', Array.isArray(list) ? [...list] : [])
    },
    fillTransferExample() {
      this.$set(this.hmcConfig.transfer, 'text', HMC_TRANSFER_EXAMPLE)
    },
    triggerTransferUpload() {
      const input = this.$refs.transferAudioInput
      if (!input) return
      input.value = ''
      input.click()
    },
    async handleTransferAudioChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      this.uploadingTransfer = true
      try {
        const { filePath } = await uploadAudioFile(file, { message: this.$Message })
        this.$set(this.hmcConfig.transfer, 'audio_file_path', String(filePath || '').trim())
        this.$Message.success('上传成功')
      } catch (e) {
        this.$Message.error((e && e.message) || '上传失败')
      } finally {
        this.uploadingTransfer = false
      }
    },
    clearTransferAudio() {
      this.$set(this.hmcConfig.transfer, 'audio_file_path', '')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './shared.scss';

.vc-section {
  margin-bottom: 16px;
}

.vc-card {
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px;
}

.vc-card--compact {
  padding-bottom: 16px;
}

.vc-hidden-file {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.vc-hmc--collapsed {
  padding-bottom: 20px;

  .vc-hmc__head {
    margin-bottom: 0;
    min-height: 24px;
  }
}

.vc-hmc__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  min-height: 24px;
}

.vc-hmc__title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  line-height: 24px;
}

.vc-hmc__switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 24px;
}

.vc-hmc__switch-label {
  font-size: 13px;
  color: #595959;
  line-height: 24px;
}

.vc-hmc__body {
  padding-top: 4px;
}

.vc-hmc-field {
  margin-bottom: 20px;

  &--failure {
    margin-bottom: 0;
    padding-top: 20px;
    border-top: 1px solid #e8eaec;

    .vc-hmc-field__label--block {
      margin-bottom: 12px;
      text-align: left;
    }

    .vc-hmc-field__control {
      text-align: left;
    }

    ::v-deep .ivu-radio-group {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 24px;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;

    &--mode {
      justify-content: flex-start;
      align-items: center;
    }
  }

  &__label {
    margin-bottom: 10px;
    font-size: 13px;
    font-weight: 600;
    color: #262626;
    display: flex;
    align-items: center;
    gap: 6px;
    text-align: left;

    &--inline {
      margin-bottom: 0;
      flex-shrink: 0;
      line-height: 28px;
    }

    &--block {
      display: block;
      width: 100%;
    }
  }

  &__tag {
    display: inline-block;
    padding: 0 6px;
    font-size: 12px;
    font-weight: 400;
    color: #8c8c8c;
    background: #f0f0f0;
    border-radius: 2px;
    line-height: 20px;
  }

  &__control {
    width: 100%;
    text-align: left;

    &--mode {
      width: 100%;
    }
  }
}

.vc-hmc-mode-tabs {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0;
  border: none;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f7fa;
  padding: 2px;

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: 28px;
    padding: 2px 14px 0;
    font-size: 13px;
    line-height: 1;
    color: #595959;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    cursor: pointer;
    transition: color 0.2s, background 0.2s, border-color 0.2s;

    &:hover:not(.is-active) {
      color: #2d8cf0;
    }

    &.is-active {
      color: #2d8cf0;
      font-weight: 500;
      background: #fff;
      border-color: #2d8cf0;
    }
  }
}

.vc-hmc-prompt-box {
  position: relative;

  &__textarea ::v-deep textarea {
    min-height: 180px;
    font-size: 13px;
    line-height: 1.55;
    resize: vertical;
  }

  &__count {
    margin-top: 4px;
    text-align: right;
    font-size: 12px;
    color: #8c8c8c;
  }
}

.vc-hmc-transfer-wrap {
  width: 100%;
}

.vc-hmc-transfer-editor {
  position: relative;
  box-sizing: border-box;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #fff;
  min-height: 120px;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: #2d8cf0;
  }

  .vc-hmc-transfer-wrap__input {
    width: 100%;

    ::v-deep .ivu-input-wrapper {
      border: none !important;
      box-shadow: none !important;
      background: transparent !important;
    }

    ::v-deep .ivu-input {
      border: none !important;
      box-shadow: none !important;
      border-radius: 0;
      min-height: 120px;
      padding: 10px 12px 44px;
      font-size: 13px;
      line-height: 1.55;
      resize: vertical;
      color: #262626;

      &::placeholder {
        color: #c5c8ce;
      }
    }
  }
}

.vc-hmc-transfer-toolbar {
  position: absolute;
  left: 10px;
  bottom: 8px;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  pointer-events: auto;
}

.vc-hmc-transfer-count {
  margin-top: 4px;
  text-align: right;
  font-size: 12px;
  color: #8c8c8c;
}

.vc-opening-tool-btn {
  height: 28px;
  padding: 0 10px !important;
  border: 1px solid #dcdee2 !important;
  border-radius: 4px !important;
  background: #fff !important;
  color: #2d8cf0 !important;
  font-size: 12px !important;
  box-shadow: none !important;

  &:hover,
  &:focus {
    border-color: #2d8cf0 !important;
    color: #1677ff !important;
    background: #f0f9ff !important;
  }
}

.vc-hmc-transfer-tip {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 12px;
  background: #eef2ff;
  border: 1px solid #d6e0ff;
  border-radius: 4px;
  line-height: 1.5;

  &__main {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  &__icon {
    flex-shrink: 0;
    color: #5668f0;
    margin-top: 2px;
  }

  &__text {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 12px;
    color: #515a6e;
    text-align: left;
  }

  &__link {
    flex-shrink: 0;
    padding: 0;
    border: none;
    background: transparent;
    color: #5668f0;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
    line-height: 1.5;

    &:hover {
      color: #3d52e8;
    }
  }
}

.vc-audio-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 160px;
  padding: 2px 6px;
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
  border-radius: 4px;

  &--inline {
    max-width: 120px;
  }

  &__name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    color: #262626;
  }

  &__del {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: transparent;
    color: #8c8c8c;
    cursor: pointer;

    &:hover {
      color: #ed4014;
    }
  }
}
</style>
